import { useFileAccessWorker } from "./file-access-worker";
import { devMode } from "@/config";
export const useFileCache = async (id) => {
    const queue = [];
    let opfsRoot = undefined;
    try {
        opfsRoot = await navigator.storage?.getDirectory();
    }
    catch (e) {
        console.log(e);
    }
    const { fileAccessWorker, sizeMessageResponse, readerMessageResponse, closeMessageResponse, writerMessageResponse, supported: workerSupport } = await useFileAccessWorker();
    const fileHandle = await opfsRoot?.getFileHandle(id + ".test", { create: true });
    const asyncSupport = fileHandle?.createWritable != undefined;
    const storageSupport = workerSupport || asyncSupport;
    if (workerSupport) {
        console.log("file-cache: Synchronous Worker enabled");
    }
    else if (asyncSupport) {
        console.log("file-cache: Async api enabled");
    }
    else {
        console.log("file-cache: No api available - reverting to in memory cache");
    }
    const start = Date.now();
    let filesize = 0;
    const size = () => {
        return filesize;
    };
    // const estimate = await navigator.storage.estimate();
    const read = async () => {
        if (workerSupport) {
            return readChunk(0, filesize);
        }
        else {
            const file = await getFile();
            const blob = file.slice(0, file.size - 1);
            return blob;
        }
    };
    const readChunk = async (offset, size) => {
        if (!storageSupport) {
            const arr = queue.filter((p) => offset >= p.offset && offset < p.offset + p.data.size);
            offset = offset - arr[0].offset;
            const blob = new Blob(arr.reduce((parts, curr) => {
                parts.push(curr.data);
                return parts;
            }, []));
            return blob.slice(offset, offset + size);
        }
        else if (workerSupport) {
            fileAccessWorker?.postMessage({ id, action: "read", offset, size });
            const arr = await readerMessageResponse(offset);
            return new Blob([arr]);
        }
        else {
            const file = await getFile();
            const blob = file.slice(offset, Math.min(offset + size + 1, file.size));
            return blob;
        }
    };
    const getFile = async () => {
        if (!storageSupport) {
            return new Blob(queue.reduce((parts, curr) => {
                parts.push(curr.data);
                return parts;
            }, []));
        }
        else {
            let retry = 0;
            let size = 0;
            const fileHandle = await opfsRoot.getFileHandle(id, { create: true });
            const file = await fileHandle?.getFile();
            if (workerSupport) {
                fileAccessWorker?.postMessage({ id, action: "size" });
                size = await sizeMessageResponse();
            }
            else {
                size = file?.size;
            }
            while (size < filesize && retry++ < 10) {
                console.log(`File not ready ${size} != ${filesize}`);
                await new Promise((r) => setTimeout(r, 100));
            }
            if (file && file.size > 0) {
                return file;
            }
            else {
                return await read();
            }
        }
    };
    const overwrite = async (data) => {
        try {
            console.log(`Overwrite ${id} : ${filesize} -> ${data.size}`);
            filesize = data.size;
            if (!storageSupport) {
                queue.splice(0, queue.length);
                queue.push({ data, index: 0, offset: 0 });
            }
            else if (workerSupport) {
                fileAccessWorker?.postMessage({ id, action: "write", truncate: true, blob: data });
                filesize = await writerMessageResponse();
                console.log(`Overwrite ${id} : worker returned ${filesize} bytes`);
            }
            else if (opfsRoot) {
                const fileHandle = await opfsRoot.getFileHandle(id, { create: true });
                const writer = await fileHandle.createWritable({ keepExistingData: false });
                await writer.write(data);
                await writer.close();
            }
        }
        catch (e) {
            console.log(e);
            console.trace();
        }
    };
    let writeLock;
    const writeChunk = async (o) => {
        if (stopAcceptingData) {
            throw "a";
        }
        queue.push(o);
        if (!storageSupport) {
            filesize += o.data.size;
        }
        else {
            let chunk;
            try {
                if (await getLock(o.index)) {
                    if (workerSupport) {
                        while ((chunk = queue.shift())) {
                            if (devMode)
                                console.log(`${(Date.now() - start) / 1000} FS - Part ${chunk.index} : %c(SEEK x- ${chunk.offset} <=> ${filesize} -a)`, chunk.offset == filesize ? "color:green;" : "color:red;font-weight: bold;");
                            fileAccessWorker?.postMessage({ action: "write", id, blob: chunk.data });
                            const size = await writerMessageResponse();
                            filesize += size;
                            if (devMode)
                                console.log(`${(Date.now() - start) / 1000} FS - Part ${chunk.index} : Wrote ${chunk.data.size} -> ${filesize} -> Complete`);
                        }
                    }
                    else if (opfsRoot) {
                        const fileHandle = await opfsRoot.getFileHandle(id, { create: true });
                        const file = await fileHandle.getFile();
                        const writer = await fileHandle.createWritable({ keepExistingData: true });
                        const offset = file.size;
                        await writer.seek(offset);
                        while ((chunk = queue.shift())) {
                            if (devMode)
                                console.log(`${(Date.now() - start) / 1000} FS - Part ${chunk.index} : %c(SEEK x- ${chunk.offset} <=> ${filesize} -a)`, chunk.offset == filesize ? "color:green;" : "color:red;font-weight: bold;");
                            filesize += chunk.data.size;
                            await writer.write(chunk.data);
                            if (devMode)
                                console.log(`${(Date.now() - start) / 1000} FS - Part ${chunk.index} : Wrote ${chunk.data.size} -> ${filesize} -> Complete`);
                            await writer.close();
                        }
                    }
                    writeLock = undefined;
                }
                if (devMode)
                    console.log(`${(Date.now() - start) / 1000} FS - FINISHED WRITING ${id}`);
            }
            catch (e) {
                if (devMode)
                    console.log(e);
            }
        }
    };
    const getLock = async (index) => {
        await new Promise((r) => setTimeout(r, 50));
        if (!writeLock) {
            writeLock = index;
        }
        return writeLock == index;
    };
    let stopAcceptingData = false;
    const flush = async () => {
        stopAcceptingData = true;
        while (storageSupport && (writeLock || queue.length > 0)) {
            await new Promise((r) => setTimeout(r, 50));
        }
        return filesize;
    };
    const acceptData = () => {
        stopAcceptingData = false;
    };
    const remove = async () => {
        queue.splice(0, queue.length);
        filesize = 0;
        if (storageSupport) {
            if (workerSupport) {
                fileAccessWorker?.postMessage({ id, action: "close" });
                await closeMessageResponse();
            }
            if (devMode)
                console.log(`REMOVE FILE ${id}`);
            opfsRoot?.removeEntry(id);
        }
    };
    return { writeChunk, remove, readChunk, getFile, overwrite, size, flush, acceptData };
};
